import { useId } from "react";
import { useFormContext } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { ErrorMessageBanner } from "./ErrorMessageBanner";
import { NumberFieldWithStepperButtons } from "./NumberFieldWithStepperButtons";

import { t } from "i18n/i18n";
import { ClockIcon } from "mds/icons";
import { MAX_BREAKOUT_DURATION, MIN_BREAKOUT_DURATION } from "utils/breakouts";

type BreakoutDurationFieldProps = {
  className?: string;
  variant?: "default" | "compact";
  layout?: "inline" | "stacked";
  onStep?: () => void;
};

// Registers breakout_duration in the parent FormProvider. Use noValidate on the
// parent form so React Hook Form can display errors instead of browser validation.
export const BreakoutDurationField = ({
  className,
  variant = "default",
  layout = "stacked",
  onStep,
}: BreakoutDurationFieldProps) => {
  const { register } = useFormContext<{ breakout_duration: number | string }>();
  const errorId = useId();
  const isCompact = variant === "compact";
  const isInline = layout === "inline";
  const rangeError = t("error.duration_out_of_range", {
    min: MIN_BREAKOUT_DURATION,
    max: MAX_BREAKOUT_DURATION,
  });

  return (
    <div className={twMerge("flex flex-col", isInline ? "gap-2" : "gap-1", className)}>
      <div
        className={twMerge(
          isInline ? "flex justify-between" : "flex flex-col gap-1",
          isCompact && "flex-row flex-wrap items-center justify-start gap-x-3 gap-y-2 pl-2",
        )}
      >
        <div
          className={twMerge(
            "flex items-center gap-2 text-sm font-medium",
            isInline && "text-black-tint-10",
            isCompact && "text-base font-medium leading-7 text-black-tint-20",
          )}
        >
          {isInline && <ClockIcon />}
          {t("breakout_group_modal.breakout_duration")}
        </div>
        <NumberFieldWithStepperButtons
          aria-describedby={errorId}
          aria-label={t("breakout_group_modal.breakout_duration")}
          label={t("breakout_group_modal.min")}
          max={MAX_BREAKOUT_DURATION}
          min={MIN_BREAKOUT_DURATION}
          variant={variant}
          onStep={onStep}
          {...register("breakout_duration", {
            required: t("error.field_required"),
            min: { value: MIN_BREAKOUT_DURATION, message: rangeError },
            max: { value: MAX_BREAKOUT_DURATION, message: rangeError },
            validate: (value) =>
              Number.isInteger(Number(value)) || t("error.whole_number_required"),
          })}
        />
      </div>
      <ErrorMessageBanner id={errorId} name="breakout_duration" />
    </div>
  );
};
